import type { ReactElement, ReactNode } from 'react';

import { ServicesProvider } from '@/services';
import type { Services } from '@/services/types';
import { I18nProvider } from '@/ui/i18n/I18nProvider';
import { ThemeProvider } from '@/ui/theme/ThemeProvider';

/**
 * The provider stack — docs/ARCHITECTURE.md §3.
 *
 * THE ORDER IS A DEPENDENCY ORDER, not a preference:
 *
 *   ServicesProvider   nothing above it; it only holds the object `createServices()` built.
 *   I18nProvider       READS `services.storage` (`jsonfmt.locale`), so it must be inside.
 *   ThemeProvider      READS `services.theme`. Inside i18n because `ThemeToggle` renders
 *                      localised labels, and `useTheme()` throws by name rather than silently
 *                      no-op'ing when it is missing.
 *
 * All three values are referentially stable for the life of the page — one `Services` object,
 * one i18next instance built in a lazy initialiser, one memo over two string snapshots — so
 * this stack costs one render and never re-renders the tree beneath it.
 *
 * ═══ WHY `<Toaster/>` IS NOT HERE ═══
 *
 * ARCHITECTURE §3 sketches this file as `<ServicesProvider><I18nProvider><ThemeProvider>
 * <Toaster/>`, and the toast region is deliberately NOT mounted at the root. docs/DESIGN.md §8
 * turns the region `static` at ≤640px so it DISPLACES the code rather than covering it, and a
 * region mounted here has nothing to displace — it would sit over the document on exactly the
 * screens where the document has the fewest pixels to spare. It is mounted through
 * `EditorSlots.notifications`, inside the editor pane, which is `position: relative` for that
 * reason. Flagged as a documented deviation rather than a silent one.
 *
 * The toast STORE is a module singleton (`ui/notifications/toastHub.ts`), so nothing needs a
 * provider to raise a toast; only the region that renders them has a position.
 */

export interface ProvidersProps {
  readonly services: Services;
  readonly children: ReactNode;
}

export function Providers({ services, children }: ProvidersProps): ReactElement {
  return (
    <ServicesProvider services={services}>
      <I18nProvider storage={services.storage}>
        <ThemeProvider theme={services.theme}>{children}</ThemeProvider>
      </I18nProvider>
    </ServicesProvider>
  );
}

export default Providers;
